/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject} from '@angular/core';

import template from './advertiser-list-page.component.html';
import {AdminListColumn, AdminResourceListPageBase} from '../common/controller/admin-resource-list-page-base';
import {formatAdnStat} from '../common/filters/format-adn-stat';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {visiblePageNumbers} from '../common/directives/pagination/visible-page-numbers';

@Component({
  standalone: false,
  selector: 'adn-advertiser-list-page',
  template
})
export class AdvertiserListPageComponent extends AdminResourceListPageBase {
  readonly itemStateName = 'app.adv.advertisers.advertiser';
  readonly teamItemStateName = 'app.adm.teams.team';
  readonly orderItemStateName = 'app.adv.orders.order';
  readonly userItemStateName = 'app.adm.users.user';

  readonly childColumns: AdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'team', sortable: false},
    {param: '', label: 'salesperson', sortable: false},
    {param: '', label: 'adOps', sortable: false}
  ];

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly appLocale: AppLocaleService
  ) {
    super(LocalNetworkProfile);
  }

  get visibleColumns(): AdminListColumn[] {
    const fields = this.list?.sel?.outputFields?.length
      ? this.list.sel.outputFields
      : (this.list?.defaultFields || []);
    return this.normalizeColumns(fields);
  }

  isFieldVisible(param: string): boolean {
    return this.isListFieldVisible(param);
  }

  toggleChildren(item: any): void {
    this.list.getChildren?.(item);
  }

  childList(itemId: string): any {
    return this.list.children?.[itemId];
  }

  changeChildPage(childList: any, page: number): void {
    if (!childList || page < 1 || page > this.totalPagesFor(childList) || page === childList.currentPage) {
      return;
    }
    childList.currentPage = page;
    childList.changePage();
  }

  totalPagesFor(childList: any): number {
    return Math.max(1, Math.ceil(childList.totalCount / childList.pageSize));
  }

  pageNumbersFor(childList: any): number[] {
    return visiblePageNumbers(childList.currentPage, this.totalPagesFor(childList));
  }

  pageRangeStartFor(childList: any): number {
    return childList.pageSize * (childList.currentPage - 1) + 1;
  }

  pageRangeEndFor(childList: any): number {
    return Math.min(childList.pageSize * childList.currentPage, childList.totalCount);
  }

  businessLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_BUSINESS !== true;
  }

  userLinkDisabled(): boolean {
    const perms = this.list?.perms || {};
    return perms.MANAGE_SYSTEM !== true
      && perms.AX_ADVERTISER_ADMIN !== true
      && perms.AX_ADVERTISER_TEAM_ADMIN !== true;
  }

  formatStat(value: any, field?: string): string {
    return formatAdnStat(value, field, undefined, undefined, this.LocalNetworkProfile.get('defaultCurrency'), this.appLocale.getLocaleId());
  }

  deliveryStat(item: any, period: string, field: string): any {
    return item?.[period]?.[field];
  }

  protected afterListInit(): void {
    if (!this.list) {
      return;
    }
    this.list.canExpand = true;
  }
}
